// Shared Hold-to-Speak component (Web Speech API, hi-IN default). Zero-cost, browser-only.
// Usage: <HoldToSpeak onTranscript={(text) => setForm({...})} label="Bolo" lang="hi-IN" />
import React, { useRef, useState } from 'react';
import { toast } from 'sonner';

export default function HoldToSpeak({ onTranscript, disabled, label = 'Hold to Speak', lang = 'hi-IN', className = '' }) {
  const [listening, setListening] = useState(false);
  const [transcript, setTranscript] = useState('');
  const recRef = useRef(null);
  const activeRef = useRef(false);
  const finalRef = useRef('');
  const start = () => {
    if (disabled || activeRef.current) return;
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SR) { toast.error('🎤 Voice not supported (Chrome/Edge/Safari me hi chalega)'); return; }
    try {
      const rec = new SR();
      rec.lang = lang; rec.interimResults = true; rec.continuous = true;
      finalRef.current = '';
      rec.onstart = () => { activeRef.current = true; setListening(true); setTranscript(''); };
      rec.onresult = (e) => {
        // Rebuild finals + interim from scratch each event — avoids duplication when
        // Chrome re-fires the same result index or finalises an interim segment.
        let final = ''; let interim = '';
        for (let i = 0; i < e.results.length; i++) {
          const t = e.results[i][0].transcript;
          if (e.results[i].isFinal) final += t + ' '; else interim += t;
        }
        finalRef.current = final.trim();
        setTranscript((finalRef.current + ' ' + interim).replace(/\s+/g, ' ').trim());
      };
      rec.onerror = (ev) => { activeRef.current = false; setListening(false); if (ev.error !== 'aborted') toast.error(`🎤 ${ev.error}`); };
      rec.onend = () => {
        activeRef.current = false; setListening(false);
        const text = (finalRef.current || '').trim();
        if (text) onTranscript?.(text); else toast.error('Kuch bola nahi 🤔');
      };
      recRef.current = rec; rec.start();
    } catch { toast.error('🎤 Voice start failed'); }
  };
  const stop = () => { try { recRef.current?.stop(); } catch { /* noop */ } };
  return (
    <div className={`flex items-center gap-2 ${className}`} data-testid="hold-to-speak-wrap">
      <button
        type="button"
        onPointerDown={start}
        onPointerUp={stop}
        onPointerLeave={() => { if (listening) stop(); }}
        onContextMenu={(e) => e.preventDefault()}
        disabled={disabled}
        data-testid="hold-to-speak-btn"
        className={`select-none flex items-center gap-1.5 px-3 py-2 rounded-xl text-xs font-black shadow-lg transition ${listening ? 'bg-red-500 text-white animate-pulse scale-105' : 'bg-gradient-to-r from-rose-500 to-fuchsia-500 text-white'}`}
      >
        <span className="text-lg leading-none">{listening ? '🔴' : '🎤'}</span>
        {listening ? 'Bolte raho…' : label}
      </button>
      {transcript && (
        <div className="text-[10px] px-2 py-1 rounded bg-slate-800 border border-slate-700 max-w-[220px] truncate italic text-slate-200">"{transcript}"</div>
      )}
    </div>
  );
}
